// src/app/contexts/adminTenant/Provider.jsx
import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useQueryClient } from '@tanstack/react-query';

import { AdminTenantContext } from './context';
import { clearTenantQueryCache } from 'utils/tenantQueryCache';

const STORAGE_KEY = 'henddu_admin_selected_tenant';

const readStoredTenant = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.id ? parsed : null;
  } catch {
    return null;
  }
};

export function AdminTenantProvider({ children }) {
  const queryClient = useQueryClient();
  const [selectedTenant, setSelectedTenantState] = useState(readStoredTenant);
  const previousTenantIdRef = useRef(selectedTenant?.id ?? null);
  const skipTenantCacheResetRef = useRef(true);

  const resetQueryCacheForTenantChange = useCallback(() => {
    clearTenantQueryCache(queryClient);
  }, [queryClient]);

  // Drop cached API data whenever the active admin tenant changes (same tab).
  // Skips the initial mount so a page refresh does not unnecessarily clear cache.
  useEffect(() => {
    const currentTenantId = selectedTenant?.id ?? null;

    if (skipTenantCacheResetRef.current) {
      skipTenantCacheResetRef.current = false;
      previousTenantIdRef.current = currentTenantId;
      return;
    }

    if (previousTenantIdRef.current !== currentTenantId) {
      resetQueryCacheForTenantChange();
      previousTenantIdRef.current = currentTenantId;
    }
  }, [selectedTenant?.id, resetQueryCacheForTenantChange]);

  // Keep state in sync with storage updates from other tabs and bust cache there too.
  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key !== STORAGE_KEY) return;
      const nextTenant = readStoredTenant();
      const nextId = nextTenant?.id ?? null;
      if (nextId !== previousTenantIdRef.current) {
        previousTenantIdRef.current = nextId;
        setSelectedTenantState(nextTenant);
        resetQueryCacheForTenantChange();
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [resetQueryCacheForTenantChange]);

  const selectTenant = useCallback((tenant) => {
    if (!tenant || !tenant.id) return;
    const payload = {
      id: tenant.id,
      name: tenant.name ?? '',
    };
    // localStorage first so axios/fetch interceptors read the new tenant
    // before any query refetch triggered by cache reset.
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    setSelectedTenantState(payload);
  }, []);

  const clearSelectedTenant = useCallback(() => {
    window.localStorage.removeItem(STORAGE_KEY);
    setSelectedTenantState(null);
    resetQueryCacheForTenantChange();
  }, [resetQueryCacheForTenantChange]);

  return (
    <AdminTenantContext.Provider
      value={{
        selectedTenant,
        selectTenant,
        clearSelectedTenant,
        hasSelectedTenant: !!selectedTenant?.id,
      }}
    >
      {children}
    </AdminTenantContext.Provider>
  );
}

AdminTenantProvider.propTypes = {
  children: PropTypes.node,
};
